import axios from 'axios'
import Swal from 'sweetalert2'
import { ref, computed, watch, inject } from 'vue'
import Mango from './mango'
import { isEqual, cloneDeep } from 'lodash'
import { diff } from 'deep-diff'
import { useCheckoutStore } from '../stores/store.js'

// Initialize store lazily when functions are called rather than at import time
const getCheckout = () => useCheckoutStore()

let couponWatcherEnabled = true

let getLocalCart = async () => {
	let localCart = window.localStorage.getItem('cart')
	localCart = localCart ? JSON.parse(localCart) : null
	let completed = await orderIsComplete(localCart)
	let exists = await cartExists(localCart)
	if (!completed && !!exists) return localCart
	else window.localStorage.removeItem('cart')
}

let getDbCart = async (user) => {
	let dbCart = window?.cart || user?.id ? (await Mango.carts({ search: { author: user.id }, sort: { updated: -1 }, depthLimit: 2 }))?.[0] : null
	let completed = await orderIsComplete(dbCart)
	let exists = await cartExists(dbCart)
	if (!completed && !!exists) return dbCart
	else await Mango.carts.delete(dbCart?.id)
}

// Check if the BC cart ID has already been converted to an order
let orderIsComplete = async (cart) => {
	if (!cart?.bcId) return false
	const orders = await Mango.orders({ search: { cartId: cart.bcId } })
	return !!orders.length
}

// If the cart has a mango ID, verify that ID exists in the database
let cartExists = async (cart) => {
	if (!cart?.id) return true
	let mangoCart = await Mango.carts({ search: { id: cart?.id }, depthLimit: 2 })?.[0]
	return !!mangoCart?.id
}

let simplifyCartItems = (cartItems) => {
	return cartItems.map((ci) => ({
		quantity: ci.quantity,
		item: ci.item?.id || ci.item,
		variant: ci.variant?.id || ci.variant,
		optional: ci?.optional?.coveredProductCode || null,
	}))
}

let processCart = async (cart, user, api) => {
	let success, data, warnings
	let guest = !user?.id
	let cartItems = cart.value?.cartItems?.map((ci) => ({
		quantity: ci.quantity,
		item: ci.item?.id || ci.item,
		variant: ci.variant?.id || ci.variant,
		optional: ci?.optional || null,
	}))
	let certificates = cart.value?.certificates

	let coupon = cart.value?.coupon || { code: '' }
	let couponDiscount = cart.value?.couponDiscount || 0
	let couponType = cart.value?.couponType || null
	let certificate = cart.value?.certificate || null
	let certificateDiscount = cart.value?.certificateDiscount || 0

	if (guest) {
		if (!cart.value?.cartItems?.length && !cart.value?.certificates?.length) return { success: true, data: null, warnings: null }

		let cartsUrl = `${api}/controllers/bigCommerce/sync/carts`
		let response = await axios.post(cartsUrl, cart.value)

		// console.log('response', response)

		warnings = response?.data?.message
		success = response?.data?.success
		data = response?.data?.data

		// console.log('processCart data', data)

		if (response?.data?.addCouponError) warnings = response.data.addCouponError

		if (data?.coupons?.length) {
			// If a coupon is applied, "cart_amount" is replaced with "cart_amount_inc_tax" and "cart_amount_ex_tax"

			let coupon = data.coupons[0]

			// console.log('Checkout Coupon: ', coupon)

			data = {
				subTotal: data?.base_amount,
				total: data?.cart_amount_inc_tax,
				tax: data?.cart_amount_inc_tax - data?.cart_amount_ex_tax,
				bcId: data?.id,
				couponDiscount: coupon.discounted_amount,
				couponType: coupon.coupon_type,
			}
		} else {
			data = {
				subTotal: data?.base_amount,
				total: data?.cart_amount,
				tax: data?.cart_amount - data?.base_amount,
				bcId: data?.id,
			}
		}
	} else {
		let cartData = {
			cartItems,
			certificates,
			id: cart?.value?.id,
			coupon,
			couponDiscount,
			couponType,
			certificate,
			certificateDiscount,
			email: user?.email,
			firstName: user?.firstName,
			lastName: user?.lastName,
		}
		let response = await Mango.carts.save(cartData, false, true)

		// if (!response?.response?.id) {
		// 	delete cartData.id
		// 	response = await Mango.carts.save(cartData, false, true)
		// }

		data = response?.response
		warnings = response?.warnings
		success = !warnings
	}
	// console.log('Update cart success: ', success)
	// console.log('Update cart warnings: ', warnings)
	return { success, data, warnings }
}

const initCart = async (user, api) => {
	let localCart = await getLocalCart()
	let dbCart = await getDbCart(user)

	const savingCart = ref(false)

	const cart = ref(
		localCart?.cartItems?.length
			? localCart
			: dbCart?.id
				? dbCart
				: {
						cartItems: [],
						certificates: [],
						coupon: {
							code: '',
						},
						couponDiscount: 0,
						couponType: false,
						certificate: null,
						certificateDiscount: 0,
					},
	)

	const originalCart = ref(cloneDeep(cart.value))

	const itemsCount = computed(() => cart.value.cartItems?.reduce((acc, ci) => acc + (ci?.quantity || 0), 0))
	const certificatesCount = computed(() => cart.value?.certificates?.reduce?.((acc, cert) => acc + (cert?.quantity || 0), 0)) || 0
	const cartTotalItems = computed(() => itemsCount.value + certificatesCount.value)
	const cartSubtotal = computed(() => cart.value.cartItems?.reduce((acc, ci) => acc + ci.quantity * (ci?.variant?.currentPrice || ci?.item?.currentPrice || 0), 0))
	const texasStateSalesTax = computed(() => cartSubtotal.value * 0.0825)
	const isDigitalOnly = computed(() => !cart.value.cartItems?.length && !!cart.value?.certificates?.length)

	// const cartTotal = computed(() => cartSubtotal.value + texasStateSalesTax.value)

	// Debugging statements
	// console.log('cartSubtotal:', cartSubtotal.value)
	// console.log('texasStateSalesTax:', texasStateSalesTax.value)

	const cartTotal = computed(() => {
		const subtotal = cartSubtotal.value
		const tax = texasStateSalesTax.value
		// console.log('subtotal:', subtotal)
		// console.log('tax:', tax)
		return subtotal + tax
	})
	//console.log('cartTotal:', cartTotal.value)

	const cartItems = computed(() => cart.value.cartItems)
	const certificates = computed(() => cart.value.certificates) // certificate(s) in the cart being purchased
	const coupon = computed(() => cart.value.coupon?.code || '')
	const certificate = computed(() => cart.value.certificate) // single certificate balance applied to cart total for a discount

	async function saveCart() {
		const checkout = getCheckout()
		checkout.updateLoading(true)
		// console.log('checkout.loading: ', checkout.loading)
		// console.log('SAVE CART TRIGGERED!!')
		couponWatcherEnabled = false
		savingCart.value = true

		let { success, data, warnings } = await processCart(cart, user, api)

		if (warnings?.includes('Coupon') || warnings?.includes('coupon')) {
			// console.log('Removing coupon from cart...')
			data.coupon = { code: '' }
			Swal.fire('Invalid Coupon', `${warnings || 'An error occurred while applying this coupon to your cart.'}`, 'warning')
		} else if (warnings || !success) {
			cart.value = cloneDeep(originalCart.value)
			savingCart.value = false

			for (let warning of warnings) {
				if (warning?.includes('insufficient stock')) {
					console.log('Out of stock item in cart. Removing...')
					return Swal.fire('Out of Stock', `${warnings || 'An error occurred while updating your cart.'}`, 'warning')
				}
			}

			return Swal.fire('Cart Error', `${warnings || 'An error occurred while updating your cart.'}`, 'warning')
		}

		let mangoCart = data

		// console.log('mangoCart', mangoCart)

		// Write back totals to the Vue store cart:
		cart.value.tax = mangoCart?.tax || 0
		cart.value.subTotal = mangoCart?.subTotal || 0
		cart.value.total = mangoCart?.total || 0
		cart.value.bcId = mangoCart?.bcId

		cart.value.couponDiscount = mangoCart?.couponDiscount || 0
		cart.value.couponType = mangoCart?.couponType || null

		if (!mangoCart?.couponType) {
			// Guest coupon is a different object from the mongo coupon object
			// coupon is added to the vue store cart when the code is applied (triggering the coupon watcher)
			// so only add the coupon if it doesn't exist in the cart already
			cart.value.coupon = mangoCart?.coupon || { code: '' }
		}

		window.localStorage.setItem('cart', JSON.stringify(cart.value))
		originalCart.value = cloneDeep(cart.value)
		savingCart.value = false
		couponWatcherEnabled = true

		// We use the same checkout instance from above
		checkout.updateLoading(false)
		// console.log('checkout.loading: ', checkout.loading)
	}

	let timer
	const debounceSave = computed(() => {
		return (...args) => {
			savingCart.value = true

			// Add things here that you want to be immediately invalidated on change
			cart.value.tax = null
			cart.value.total = null

			clearTimeout(timer)
			timer = setTimeout(() => {
				saveCart.apply(this, args)
			}, 1200)
		}
	})

	watch(
		cartItems,
		(ci, oci) => {
			window.localStorage.setItem('cart', JSON.stringify(cart.value))

			// Check to see if anything actually changed:
			if (user?.id) {
				let newCi = simplifyCartItems(ci)
				let oldCi = simplifyCartItems(originalCart.value.cartItems)
				let differences = diff(newCi, oldCi)
				// console.log('differences', differences, newCi, oldCi)
				if (differences?.length) debounceSave.value()
			} else {
				debounceSave.value()
			}
		},
		{ deep: true },
	)

	watch(
		certificates,
		(certs, oc) => {
			window.localStorage.setItem('cart', JSON.stringify(cart.value))
			// let oldCerts = originalCart.value.certificates
			// let differences = diff(certs, oldCerts)
			// console.log('differences - certs', differences)
			// console.log('certs', certs)
			// console.log('oldCerts', oldCerts)
			// if (differences)
			debounceSave.value() // certs are not an actual BigCommerce product nor their own collection, so any change to cart.value.certificates should be legit
		},
		{ deep: true },
	)

	watch(coupon, (nc, oc) => {
		if (!couponWatcherEnabled) return
		window.localStorage.setItem('cart', JSON.stringify(cart.value))
		// debounceSave.value()
	})

	watch(certificate, (nc, oc) => {
		window.localStorage.setItem('cart', JSON.stringify(cart.value))
		// debounceSave.value()
	})

	let totalItems = cart.value?.cartItems?.reduce((acc, ci) => acc + ci.quantity, 0)
	if (totalItems && !dbCart && user?.id) saveCart()

	return {
		cart,
		itemsCount,
		certificatesCount,
		cartTotalItems,
		cartSubtotal,
		savingCart,
		saveCart,
		texasStateSalesTax,
		cartTotal,
		originalCart,
		isDigitalOnly,
	}
}

export default initCart
